/**
 * Candado de la sesión de Supabase que vive SÓLO en esta pestaña.
 *
 * Por defecto supabase-js usa `navigator.locks`, que es compartido entre todas las pestañas
 * del mismo origen, y arranca esperándolo sin límite (`_acquireLock(-1, …)`). Si otra pestaña
 * de Atlas lo deja tomado —iOS congela las pestañas de fondo y el navegador de Instagram abre
 * Google en una vista aparte—, la pestaña que vuelve de Google con el token en la URL se queda
 * esperando para siempre: el login se ve, el usuario toca Google otra vez y nunca entra.
 * Ver ADR-2026-10-15-el-login-con-google-no-espera-a-otras-pestanas.
 *
 * Es el `processLock` de `@supabase/auth-js` (que la versión instalada no exporta), con la
 * misma semántica de `acquireTimeout`:
 * - negativo: espera su turno;
 * - 0: si está ocupado falla en el acto con `isAcquireTimeout` (el refresco automático lo
 *   pide así y, ante ese error, sólo saltea el tick);
 * - positivo: espera hasta ese tiempo y después falla igual.
 *
 * Lo que se pierde es la coordinación entre pestañas: dos pestañas pueden refrescar el token
 * a la vez. Supabase tolera reusar el refresh token dentro de su intervalo de gracia.
 */

class CandadoOcupadoError extends Error {
  readonly isAcquireTimeout = true;
}

const enCurso: Record<string, Promise<unknown>> = {};

export async function candadoDeLaPestana<R>(
  nombre: string,
  acquireTimeout: number,
  fn: () => Promise<R>,
): Promise<R> {
  const anterior = enCurso[nombre] ?? Promise.resolve();

  const esperas: Promise<unknown>[] = [anterior.catch(() => null)];
  if (acquireTimeout >= 0) {
    esperas.push(
      new Promise((_, reject) => {
        setTimeout(
          () => reject(new CandadoOcupadoError(`El candado "${nombre}" está ocupado`)),
          acquireTimeout,
        );
      }),
    );
  }

  const actual = Promise.race(esperas).then(() => fn());

  // El próximo espera a ESTA operación; si ésta no consiguió el candado, a la que lo tenía.
  enCurso[nombre] = actual.catch(async (e) => {
    if (e instanceof CandadoOcupadoError) await anterior.catch(() => null);
    return null;
  });

  return actual;
}
